import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { updateGpsCoordinates } from "store/surveyJS";
import questionTypes from "../../questionTypes";
import updateElementByIdFromArray from "../../utils/updateElementByIdFromArray";
import { MapCenterPoint } from "./components";
import { MapZoomLevel } from "./components/MapZoomLevel";
import appendElementToArray from "../../utils/appendElementToArray";
import { GpsCoordinatesState } from "../../stateConstructors";

const GpsCoordinatesSidebar = () => {
  const { selectedQuestionElement, gpsCoordinates } = useSelector(
    (state: RootState) => state.surveyJS,
  );
  const [currentGpsCoordinates, setCurrentGpsCoordinates] = useState<any>({});
  const dispatch = useDispatch();

  const persistCurrentState = (updatedCurrent: any) => {
    updateElementByIdFromArray(
      gpsCoordinates,
      updatedCurrent,
      (updatedGpsCoordinates: any) => {
        setCurrentGpsCoordinates(updatedCurrent);
        dispatch(updateGpsCoordinates(updatedGpsCoordinates));
      },
    );
  };

  useEffect(() => {
    if (!selectedQuestionElement) {
      return;
    }

    const selectedType = selectedQuestionElement.getType?.();
    if (selectedType !== questionTypes.gpsCoordinates) {
      return;
    }

    selectedQuestionElement.isRequired = true;

    const questionId =
      selectedQuestionElement._id ?? selectedQuestionElement.rdx_id;
    const questionName = selectedQuestionElement.name;

    let currentStateByQuestion = questionId
      ? gpsCoordinates.find((s: any) => s.id === questionId)
      : null;

    if (!currentStateByQuestion && questionName) {
      currentStateByQuestion = gpsCoordinates.find(
        (s: any) => s.name === questionName,
      );
    }

    if (!currentStateByQuestion) {
      const newState = {
        ...new GpsCoordinatesState({
          _id: questionId,
          title: selectedQuestionElement.title,
          description: selectedQuestionElement.description,
          isRequired: true,
        }).getState(),
        name: questionName,
        isRequired: true,
      };
      const updatedGpsCoordinates = [...(gpsCoordinates ?? [])];
      appendElementToArray(newState, updatedGpsCoordinates);
      dispatch(updateGpsCoordinates(updatedGpsCoordinates));

      selectedQuestionElement._id = newState.id;
      selectedQuestionElement.rdx_id = newState.id;
      setCurrentGpsCoordinates(newState);
      return;
    }

    if (!selectedQuestionElement._id || !selectedQuestionElement.rdx_id) {
      selectedQuestionElement._id = currentStateByQuestion.id;
      selectedQuestionElement.rdx_id = currentStateByQuestion.id;
    }

    if (currentStateByQuestion.isRequired !== true) {
      currentStateByQuestion = { ...currentStateByQuestion, isRequired: true };
      persistCurrentState(currentStateByQuestion);
    } else {
      setCurrentGpsCoordinates(currentStateByQuestion);
    }
  }, [selectedQuestionElement, gpsCoordinates]);

  const handleMapInputsOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedCurrentGpsCoordinates = { ...currentGpsCoordinates };

    if (name === "zoomLevel") {
      updatedCurrentGpsCoordinates.zoomLevel = parseFloat(value);
    } else {
      updatedCurrentGpsCoordinates.mapCenterPoint = {
        ...updatedCurrentGpsCoordinates.mapCenterPoint,
        doFly: false,
        [name]: value,
      };
    }

    persistCurrentState(updatedCurrentGpsCoordinates);
  };

  const handleClickOnLocationNavigatorButton = () => {
    const updatedCurrentGpsCoordinates = {
      ...currentGpsCoordinates,
      mapCenterPoint: {
        ...currentGpsCoordinates.mapCenterPoint,
        doFly: true,
      },
    };

    persistCurrentState(updatedCurrentGpsCoordinates);
  };

  const handleClickOnZoomLevelBtn = () => {
    const updatedCurrentGpsCoordinates = { ...currentGpsCoordinates };
    if (isNaN(updatedCurrentGpsCoordinates.zoomLevel)) {
      return;
    }

    updatedCurrentGpsCoordinates.mapCenterPoint = {
      ...updatedCurrentGpsCoordinates.mapCenterPoint,
      doFly: true,
    };

    persistCurrentState(updatedCurrentGpsCoordinates);
  };

  return (
    <div className="flex flex-col py-[40px] px-[32px] bg-[#F3F5F7] w-full h-full gap-8">
      <MapCenterPoint
        handleInputsOnChange={handleMapInputsOnChange}
        handleClickOnLocationNavigatorButton={
          handleClickOnLocationNavigatorButton
        }
        mapCenterPoint={currentGpsCoordinates.mapCenterPoint}
      />
      <MapZoomLevel
        handleInputsOnChange={handleMapInputsOnChange}
        handleClickOnZoomLevelBtn={handleClickOnZoomLevelBtn}
        zoomLevel={currentGpsCoordinates.zoomLevel}
      />
    </div>
  );
};

export { GpsCoordinatesSidebar };
